import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Plus, Trash2, ChevronDown, ChevronRight, Copy, Check,
  Play, FileCode2, Columns3, Key, Link2, ShieldCheck, Loader2,
  GripVertical, ArrowUp, ArrowDown,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { ResizeHandle } from '@/components/layout/ResizeHandle';
import type { TableDesignerTab, TableMeta } from '@/types';

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

interface DesignerColumn {
  id: string;
  name: string;
  type: string;
  notNull: boolean;
  defaultValue: string;
  isPrimaryKey: boolean;
  comment: string;
}

interface DesignerIndex {
  id: string;
  name: string;
  unique: boolean;
  columns: Array<{ column: string; direction: 'ASC' | 'DESC' }>;
}

interface DesignerForeignKey {
  id: string;
  name: string;
  columns: string[];
  refTable: string;
  refColumns: string[];
  onDelete: string;
  onUpdate: string;
}

interface DesignerCheck {
  id: string;
  name: string;
  expression: string;
}

interface DesignerState {
  tableName: string;
  schema: string;
  columns: DesignerColumn[];
  indexes: DesignerIndex[];
  foreignKeys: DesignerForeignKey[];
  checks: DesignerCheck[];
}

type TreeSelection =
  | { section: 'column'; id: string }
  | { section: 'index'; id: string }
  | { section: 'fk'; id: string }
  | { section: 'check'; id: string }
  | null;

interface TableStructureResponse extends TableMeta {
  ddl: string;
}

// ═══════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════

const COMMON_TYPES = [
  'INTEGER', 'BIGINT', 'SERIAL', 'SMALLINT', 'BOOLEAN',
  'VARCHAR(255)', 'TEXT', 'CHAR(1)',
  'DATE', 'TIME', 'TIMESTAMP', 'TIMESTAMPTZ',
  'JSON', 'JSONB', 'UUID',
  'FLOAT', 'DOUBLE PRECISION', 'DECIMAL', 'NUMERIC',
  'BYTEA', 'ARRAY',
];

const FK_ACTIONS = ['NO ACTION', 'CASCADE', 'SET NULL', 'SET DEFAULT', 'RESTRICT'];

// ═══════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function createEmptyColumn(): DesignerColumn {
  return {
    id: `col-${uid()}`,
    name: '',
    type: 'VARCHAR(255)',
    notNull: false,
    defaultValue: '',
    isPrimaryKey: false,
    comment: '',
  };
}

function createEmptyIndex(tableName: string): DesignerIndex {
  return {
    id: `idx-${uid()}`,
    name: `idx_${tableName || 'table'}_`,
    unique: false,
    columns: [],
  };
}

function createEmptyFK(tableName: string): DesignerForeignKey {
  return {
    id: `fk-${uid()}`,
    name: `fk_${tableName || 'table'}_`,
    columns: [],
    refTable: '',
    refColumns: [],
    onDelete: 'NO ACTION',
    onUpdate: 'NO ACTION',
  };
}

function createEmptyCheck(tableName: string): DesignerCheck {
  return {
    id: `chk-${uid()}`,
    name: `chk_${tableName || 'table'}_`,
    expression: '',
  };
}

/** Quote a SQL identifier */
function q(name: string): string {
  return '"' + name.replace(/"/g, '""') + '"';
}

// ═══════════════════════════════════════════
// SQL Generation
// ═══════════════════════════════════════════

function generateCreateSQL(state: DesignerState): string {
  const { tableName, schema, columns, indexes, foreignKeys, checks } = state;
  if (!tableName.trim()) return '-- Enter a table name';

  const validCols = columns.filter((c) => c.name.trim());
  if (validCols.length === 0) return '-- Add at least one column';

  const lines: string[] = [];

  for (const col of validCols) {
    let line = `  ${q(col.name)} ${col.type}`;
    if (col.notNull) line += ' NOT NULL';
    if (col.defaultValue.trim()) line += ` DEFAULT ${col.defaultValue}`;
    lines.push(line);
  }

  // Primary key
  const pks = validCols.filter((c) => c.isPrimaryKey).map((c) => q(c.name));
  if (pks.length > 0) {
    lines.push(`  PRIMARY KEY (${pks.join(', ')})`);
  }

  // Foreign keys (inline)
  for (const fk of foreignKeys) {
    if (!fk.name.trim() || fk.columns.length === 0 || !fk.refTable.trim()) continue;
    const fkCols = fk.columns.map(q).join(', ');
    const refCols = fk.refColumns.map(q).join(', ');
    let line = `  CONSTRAINT ${q(fk.name)} FOREIGN KEY (${fkCols}) REFERENCES ${q(fk.refTable)}`;
    if (refCols) line += ` (${refCols})`;
    if (fk.onDelete !== 'NO ACTION') line += ` ON DELETE ${fk.onDelete}`;
    if (fk.onUpdate !== 'NO ACTION') line += ` ON UPDATE ${fk.onUpdate}`;
    lines.push(line);
  }

  // Check constraints (inline)
  for (const chk of checks) {
    if (!chk.name.trim() || !chk.expression.trim()) continue;
    lines.push(`  CONSTRAINT ${q(chk.name)} CHECK (${chk.expression})`);
  }

  const parts: string[] = [];
  parts.push(`CREATE TABLE ${q(schema)}.${q(tableName)} (\n${lines.join(',\n')}\n);`);

  // Indexes (separate statements)
  for (const idx of indexes) {
    if (!idx.name.trim() || idx.columns.length === 0) continue;
    const colParts = idx.columns
      .filter((c) => c.column.trim())
      .map((c) => `${q(c.column)} ${c.direction}`);
    if (colParts.length === 0) continue;
    const uniqueStr = idx.unique ? 'UNIQUE ' : '';
    parts.push(
      `CREATE ${uniqueStr}INDEX ${q(idx.name)} ON ${q(schema)}.${q(tableName)} (${colParts.join(', ')});`,
    );
  }

  return parts.join('\n\n');
}

function generateAlterSQL(state: DesignerState, original: DesignerState): string {
  const stmts: string[] = [];
  const tgt = `${q(state.schema)}.${q(state.tableName)}`;

  // Rename table
  if (state.tableName !== original.tableName) {
    stmts.push(`ALTER TABLE ${q(original.schema)}.${q(original.tableName)} RENAME TO ${q(state.tableName)};`);
  }

  // Added columns
  const origColNames = new Set(original.columns.map((c) => c.id));
  for (const col of state.columns) {
    if (!origColNames.has(col.id) && col.name.trim()) {
      let line = `ALTER TABLE ${tgt} ADD COLUMN ${q(col.name)} ${col.type}`;
      if (col.notNull) line += ' NOT NULL';
      if (col.defaultValue.trim()) line += ` DEFAULT ${col.defaultValue}`;
      stmts.push(line + ';');
    }
  }

  // Dropped columns
  const newColIds = new Set(state.columns.map((c) => c.id));
  for (const col of original.columns) {
    if (!newColIds.has(col.id)) {
      stmts.push(`ALTER TABLE ${tgt} DROP COLUMN ${q(col.name)};`);
    }
  }

  // Modified columns (basic: type changes, nullability, default)
  for (const col of state.columns) {
    const orig = original.columns.find((c) => c.id === col.id);
    if (!orig) continue;
    if (col.type !== orig.type) {
      stmts.push(`ALTER TABLE ${tgt} ALTER COLUMN ${q(col.name)} TYPE ${col.type};`);
    }
    if (col.notNull !== orig.notNull) {
      stmts.push(
        col.notNull
          ? `ALTER TABLE ${tgt} ALTER COLUMN ${q(col.name)} SET NOT NULL;`
          : `ALTER TABLE ${tgt} ALTER COLUMN ${q(col.name)} DROP NOT NULL;`,
      );
    }
    if (col.defaultValue !== orig.defaultValue) {
      stmts.push(
        col.defaultValue.trim()
          ? `ALTER TABLE ${tgt} ALTER COLUMN ${q(col.name)} SET DEFAULT ${col.defaultValue};`
          : `ALTER TABLE ${tgt} ALTER COLUMN ${q(col.name)} DROP DEFAULT;`,
      );
    }
    if (col.name !== orig.name) {
      stmts.push(`ALTER TABLE ${tgt} RENAME COLUMN ${q(orig.name)} TO ${q(col.name)};`);
    }
  }

  // Dropped indexes
  const newIdxIds = new Set(state.indexes.map((i) => i.id));
  for (const idx of original.indexes) {
    if (!newIdxIds.has(idx.id)) {
      stmts.push(`DROP INDEX ${q(idx.name)};`);
    }
  }

  // Added indexes
  const origIdxIds = new Set(original.indexes.map((i) => i.id));
  for (const idx of state.indexes) {
    if (!origIdxIds.has(idx.id) && idx.name.trim() && idx.columns.length > 0) {
      const colParts = idx.columns
        .filter((c) => c.column.trim())
        .map((c) => `${q(c.column)} ${c.direction}`);
      if (colParts.length === 0) continue;
      const uniqueStr = idx.unique ? 'UNIQUE ' : '';
      stmts.push(`CREATE ${uniqueStr}INDEX ${q(idx.name)} ON ${tgt} (${colParts.join(', ')});`);
    }
  }

  // Dropped FKs
  const newFkIds = new Set(state.foreignKeys.map((f) => f.id));
  for (const fk of original.foreignKeys) {
    if (!newFkIds.has(fk.id)) {
      stmts.push(`ALTER TABLE ${tgt} DROP CONSTRAINT ${q(fk.name)};`);
    }
  }

  // Added FKs
  const origFkIds = new Set(original.foreignKeys.map((f) => f.id));
  for (const fk of state.foreignKeys) {
    if (!origFkIds.has(fk.id) && fk.name.trim() && fk.columns.length > 0 && fk.refTable.trim()) {
      const fkCols = fk.columns.map(q).join(', ');
      const refCols = fk.refColumns.map(q).join(', ');
      let line = `ALTER TABLE ${tgt} ADD CONSTRAINT ${q(fk.name)} FOREIGN KEY (${fkCols}) REFERENCES ${q(fk.refTable)}`;
      if (refCols) line += ` (${refCols})`;
      if (fk.onDelete !== 'NO ACTION') line += ` ON DELETE ${fk.onDelete}`;
      if (fk.onUpdate !== 'NO ACTION') line += ` ON UPDATE ${fk.onUpdate}`;
      stmts.push(line + ';');
    }
  }

  // Dropped checks
  const newChkIds = new Set(state.checks.map((c) => c.id));
  for (const chk of original.checks) {
    if (!newChkIds.has(chk.id)) {
      stmts.push(`ALTER TABLE ${tgt} DROP CONSTRAINT ${q(chk.name)};`);
    }
  }

  // Added checks
  const origChkIds = new Set(original.checks.map((c) => c.id));
  for (const chk of state.checks) {
    if (!origChkIds.has(chk.id) && chk.name.trim() && chk.expression.trim()) {
      stmts.push(`ALTER TABLE ${tgt} ADD CONSTRAINT ${q(chk.name)} CHECK (${chk.expression});`);
    }
  }

  if (stmts.length === 0) return '-- No changes detected';
  return stmts.join('\n');
}

// ═══════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════

const inputClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors';

const selectClass =
  'w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary outline-none focus:border-focus transition-colors appearance-none cursor-pointer';

const labelClass = 'block text-xs font-semibold text-text-secondary mb-1.5';

// ═══════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover rounded-sm transition-colors"
      title="Copy SQL"
    >
      {copied ? <Check size={13} className="text-accent-green" /> : <Copy size={13} />}
      {copied ? 'Copied' : 'Copy SQL'}
    </button>
  );
}

// ═══════════════════════════════════════════
// Left Pane: Object Tree
// ═══════════════════════════════════════════

interface ObjectTreeProps {
  state: DesignerState;
  selection: TreeSelection;
  onSelect: (sel: TreeSelection) => void;
  onAddColumn: () => void;
  onAddIndex: () => void;
  onAddFK: () => void;
  onAddCheck: () => void;
}

function ObjectTree({ state, selection, onSelect, onAddColumn, onAddIndex, onAddFK, onAddCheck }: ObjectTreeProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    columns: true,
    indexes: true,
    fks: true,
    checks: true,
  });

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const isSelected = useCallback(
    (section: string, id: string) => {
      if (!selection) return false;
      return selection.section === section && selection.id === id;
    },
    [selection],
  );

  return (
    <div className="h-full overflow-y-auto py-1">
      {/* Columns */}
      <div>
        <div
          className="flex items-center justify-between px-2 py-1 cursor-pointer hover:bg-dbgo-tab-hover group"
          onClick={() => toggleSection('columns')}
        >
          <div className="flex items-center gap-1">
            {expandedSections.columns ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <Columns3 size={13} className="text-accent-blue" />
            <span className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
              Columns
            </span>
            <span className="text-2xs text-text-muted">({state.columns.length})</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddColumn();
            }}
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted opacity-0 group-hover:opacity-100 hover:text-accent-blue hover:bg-accent-blue/10 transition-all"
            title="Add Column"
          >
            <Plus size={12} />
          </button>
        </div>
        {expandedSections.columns &&
          state.columns.map((col) => (
            <button
              key={col.id}
              onClick={() => onSelect({ section: 'column', id: col.id })}
              className={`flex items-center gap-1.5 w-full text-left px-3 py-[5px] pl-7 text-xs transition-colors ${
                isSelected('column', col.id)
                  ? 'bg-accent-blue/20 text-text-primary'
                  : 'text-text-secondary hover:bg-dbgo-tab-hover hover:text-text-primary'
              }`}
            >
              {col.isPrimaryKey ? (
                <Key size={12} className="text-accent-yellow flex-shrink-0" />
              ) : (
                <div className="w-3 h-3 flex-shrink-0" />
              )}
              <span className="truncate font-mono">
                {col.name || <span className="italic text-text-muted">unnamed</span>}
              </span>
              {col.type && (
                <span className="text-2xs text-text-muted ml-auto flex-shrink-0">{col.type}</span>
              )}
            </button>
          ))}
      </div>

      {/* Indexes */}
      <div className="mt-1">
        <div
          className="flex items-center justify-between px-2 py-1 cursor-pointer hover:bg-dbgo-tab-hover group"
          onClick={() => toggleSection('indexes')}
        >
          <div className="flex items-center gap-1">
            {expandedSections.indexes ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <GripVertical size={13} className="text-accent-purple" />
            <span className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
              Indexes
            </span>
            <span className="text-2xs text-text-muted">({state.indexes.length})</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddIndex();
            }}
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted opacity-0 group-hover:opacity-100 hover:text-accent-blue hover:bg-accent-blue/10 transition-all"
            title="Add Index"
          >
            <Plus size={12} />
          </button>
        </div>
        {expandedSections.indexes &&
          state.indexes.map((idx) => (
            <button
              key={idx.id}
              onClick={() => onSelect({ section: 'index', id: idx.id })}
              className={`flex items-center gap-1.5 w-full text-left px-3 py-[5px] pl-7 text-xs transition-colors ${
                isSelected('index', idx.id)
                  ? 'bg-accent-blue/20 text-text-primary'
                  : 'text-text-secondary hover:bg-dbgo-tab-hover hover:text-text-primary'
              }`}
            >
              <span className="truncate font-mono">
                {idx.name || <span className="italic text-text-muted">unnamed</span>}
              </span>
              {idx.unique && (
                <span className="text-2xs text-accent-yellow ml-auto flex-shrink-0">UNIQUE</span>
              )}
            </button>
          ))}
      </div>

      {/* Foreign Keys */}
      <div className="mt-1">
        <div
          className="flex items-center justify-between px-2 py-1 cursor-pointer hover:bg-dbgo-tab-hover group"
          onClick={() => toggleSection('fks')}
        >
          <div className="flex items-center gap-1">
            {expandedSections.fks ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <Link2 size={13} className="text-accent-teal" />
            <span className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
              Foreign Keys
            </span>
            <span className="text-2xs text-text-muted">({state.foreignKeys.length})</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddFK();
            }}
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted opacity-0 group-hover:opacity-100 hover:text-accent-blue hover:bg-accent-blue/10 transition-all"
            title="Add Foreign Key"
          >
            <Plus size={12} />
          </button>
        </div>
        {expandedSections.fks &&
          state.foreignKeys.map((fk) => (
            <button
              key={fk.id}
              onClick={() => onSelect({ section: 'fk', id: fk.id })}
              className={`flex items-center gap-1.5 w-full text-left px-3 py-[5px] pl-7 text-xs transition-colors ${
                isSelected('fk', fk.id)
                  ? 'bg-accent-blue/20 text-text-primary'
                  : 'text-text-secondary hover:bg-dbgo-tab-hover hover:text-text-primary'
              }`}
            >
              <span className="truncate font-mono">
                {fk.name || <span className="italic text-text-muted">unnamed</span>}
              </span>
            </button>
          ))}
      </div>

      {/* Checks */}
      <div className="mt-1">
        <div
          className="flex items-center justify-between px-2 py-1 cursor-pointer hover:bg-dbgo-tab-hover group"
          onClick={() => toggleSection('checks')}
        >
          <div className="flex items-center gap-1">
            {expandedSections.checks ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <ShieldCheck size={13} className="text-accent-orange" />
            <span className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
              Checks
            </span>
            <span className="text-2xs text-text-muted">({state.checks.length})</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddCheck();
            }}
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted opacity-0 group-hover:opacity-100 hover:text-accent-blue hover:bg-accent-blue/10 transition-all"
            title="Add Check Constraint"
          >
            <Plus size={12} />
          </button>
        </div>
        {expandedSections.checks &&
          state.checks.map((chk) => (
            <button
              key={chk.id}
              onClick={() => onSelect({ section: 'check', id: chk.id })}
              className={`flex items-center gap-1.5 w-full text-left px-3 py-[5px] pl-7 text-xs transition-colors ${
                isSelected('check', chk.id)
                  ? 'bg-accent-blue/20 text-text-primary'
                  : 'text-text-secondary hover:bg-dbgo-tab-hover hover:text-text-primary'
              }`}
            >
              <span className="truncate font-mono">
                {chk.name || <span className="italic text-text-muted">unnamed</span>}
              </span>
            </button>
          ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Right Pane: Property Editors
// ═══════════════════════════════════════════

interface ColumnEditorProps {
  column: DesignerColumn;
  onChange: (updated: DesignerColumn) => void;
  onDelete: () => void;
}

function ColumnEditor({ column, onChange, onDelete }: ColumnEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-text-primary">Column Properties</h3>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 px-2 py-1 text-xs text-accent-red hover:bg-accent-red/10 rounded-sm transition-colors"
        >
          <Trash2 size={12} />
          Remove
        </button>
      </div>

      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          value={column.name}
          onChange={(e) => onChange({ ...column, name: e.target.value })}
          placeholder="column_name"
          autoFocus
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Data Type</label>
        <div className="relative">
          <input
            type="text"
            list="common-types"
            value={column.type}
            onChange={(e) => onChange({ ...column, type: e.target.value })}
            placeholder="VARCHAR(255)"
            className={inputClass}
          />
          <datalist id="common-types">
            {COMMON_TYPES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={column.notNull}
            onChange={(e) => onChange({ ...column, notNull: e.target.checked })}
            className="w-3.5 h-3.5 accent-accent-blue cursor-pointer"
          />
          Not Null
        </label>
        <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={column.isPrimaryKey}
            onChange={(e) => onChange({ ...column, isPrimaryKey: e.target.checked })}
            className="w-3.5 h-3.5 accent-accent-blue cursor-pointer"
          />
          Primary Key
        </label>
      </div>

      <div>
        <label className={labelClass}>Default Value</label>
        <input
          type="text"
          value={column.defaultValue}
          onChange={(e) => onChange({ ...column, defaultValue: e.target.value })}
          placeholder="e.g. 0, 'default', NOW()"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Comment</label>
        <input
          type="text"
          value={column.comment}
          onChange={(e) => onChange({ ...column, comment: e.target.value })}
          placeholder="Optional column comment"
          className={inputClass}
        />
      </div>
    </div>
  );
}

interface IndexEditorProps {
  index: DesignerIndex;
  columnNames: string[];
  onChange: (updated: DesignerIndex) => void;
  onDelete: () => void;
}

function IndexEditor({ index, columnNames, onChange, onDelete }: IndexEditorProps) {
  const addColumn = useCallback(() => {
    onChange({
      ...index,
      columns: [...index.columns, { column: columnNames[0] ?? '', direction: 'ASC' }],
    });
  }, [index, columnNames, onChange]);

  const removeColumn = useCallback(
    (i: number) => {
      onChange({ ...index, columns: index.columns.filter((_, idx) => idx !== i) });
    },
    [index, onChange],
  );

  const moveColumn = useCallback(
    (i: number, dir: -1 | 1) => {
      const cols = [...index.columns];
      const target = i + dir;
      if (target < 0 || target >= cols.length) return;
      [cols[i], cols[target]] = [cols[target], cols[i]];
      onChange({ ...index, columns: cols });
    },
    [index, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-text-primary">Index Properties</h3>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 px-2 py-1 text-xs text-accent-red hover:bg-accent-red/10 rounded-sm transition-colors"
        >
          <Trash2 size={12} />
          Remove
        </button>
      </div>

      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          value={index.name}
          onChange={(e) => onChange({ ...index, name: e.target.value })}
          placeholder="idx_table_column"
          className={inputClass}
        />
      </div>

      <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
        <input
          type="checkbox"
          checked={index.unique}
          onChange={(e) => onChange({ ...index, unique: e.target.checked })}
          className="w-3.5 h-3.5 accent-accent-blue cursor-pointer"
        />
        Unique
      </label>

      <div>
        <label className={labelClass}>Columns</label>
        <div className="flex flex-col gap-1.5">
          {index.columns.map((col, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <select
                value={col.column}
                onChange={(e) => {
                  const cols = [...index.columns];
                  cols[i] = { ...cols[i], column: e.target.value };
                  onChange({ ...index, columns: cols });
                }}
                className={selectClass}
              >
                <option value="">-- select --</option>
                {columnNames.map((cn) => (
                  <option key={cn} value={cn}>
                    {cn}
                  </option>
                ))}
              </select>
              <select
                value={col.direction}
                onChange={(e) => {
                  const cols = [...index.columns];
                  cols[i] = { ...cols[i], direction: e.target.value as 'ASC' | 'DESC' };
                  onChange({ ...index, columns: cols });
                }}
                className="w-20 bg-dbgo-input border rounded-sm px-1.5 py-[7px] text-[13px] text-text-primary outline-none focus:border-focus transition-colors appearance-none cursor-pointer flex-shrink-0"
              >
                <option value="ASC">ASC</option>
                <option value="DESC">DESC</option>
              </select>
              <button
                onClick={() => moveColumn(i, -1)}
                disabled={i === 0}
                className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-primary hover:bg-dbgo-tab-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <ArrowUp size={12} />
              </button>
              <button
                onClick={() => moveColumn(i, 1)}
                disabled={i === index.columns.length - 1}
                className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-primary hover:bg-dbgo-tab-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <ArrowDown size={12} />
              </button>
              <button
                onClick={() => removeColumn(i)}
                className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-accent-red hover:bg-dbgo-tab-hover transition-colors flex-shrink-0"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <button
            onClick={addColumn}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-accent-blue hover:bg-accent-blue/10 rounded-sm transition-colors self-start"
          >
            <Plus size={12} />
            Add Column
          </button>
        </div>
      </div>
    </div>
  );
}

interface FKEditorProps {
  fk: DesignerForeignKey;
  columnNames: string[];
  onChange: (updated: DesignerForeignKey) => void;
  onDelete: () => void;
}

function FKEditor({ fk, columnNames, onChange, onDelete }: FKEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-text-primary">Foreign Key Properties</h3>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 px-2 py-1 text-xs text-accent-red hover:bg-accent-red/10 rounded-sm transition-colors"
        >
          <Trash2 size={12} />
          Remove
        </button>
      </div>

      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          value={fk.name}
          onChange={(e) => onChange({ ...fk, name: e.target.value })}
          placeholder="fk_table_column"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Columns</label>
        <div className="flex flex-col gap-1.5">
          {fk.columns.map((col, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <select
                value={col}
                onChange={(e) => {
                  const cols = [...fk.columns];
                  cols[i] = e.target.value;
                  onChange({ ...fk, columns: cols });
                }}
                className={selectClass}
              >
                <option value="">-- select --</option>
                {columnNames.map((cn) => (
                  <option key={cn} value={cn}>
                    {cn}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  onChange({ ...fk, columns: fk.columns.filter((_, idx) => idx !== i) });
                }}
                className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-accent-red hover:bg-dbgo-tab-hover transition-colors flex-shrink-0"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <button
            onClick={() => onChange({ ...fk, columns: [...fk.columns, ''] })}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-accent-blue hover:bg-accent-blue/10 rounded-sm transition-colors self-start"
          >
            <Plus size={12} />
            Add Column
          </button>
        </div>
      </div>

      <div>
        <label className={labelClass}>References Table</label>
        <input
          type="text"
          value={fk.refTable}
          onChange={(e) => onChange({ ...fk, refTable: e.target.value })}
          placeholder="schema.table"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>References Columns</label>
        <div className="flex flex-col gap-1.5">
          {fk.refColumns.map((col, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                type="text"
                value={col}
                onChange={(e) => {
                  const cols = [...fk.refColumns];
                  cols[i] = e.target.value;
                  onChange({ ...fk, refColumns: cols });
                }}
                placeholder="column_name"
                className={inputClass}
              />
              <button
                onClick={() => {
                  onChange({ ...fk, refColumns: fk.refColumns.filter((_, idx) => idx !== i) });
                }}
                className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-accent-red hover:bg-dbgo-tab-hover transition-colors flex-shrink-0"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <button
            onClick={() => onChange({ ...fk, refColumns: [...fk.refColumns, ''] })}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-accent-blue hover:bg-accent-blue/10 rounded-sm transition-colors self-start"
          >
            <Plus size={12} />
            Add Reference Column
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>On Delete</label>
          <select
            value={fk.onDelete}
            onChange={(e) => onChange({ ...fk, onDelete: e.target.value })}
            className={selectClass}
          >
            {FK_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>On Update</label>
          <select
            value={fk.onUpdate}
            onChange={(e) => onChange({ ...fk, onUpdate: e.target.value })}
            className={selectClass}
          >
            {FK_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

interface CheckEditorProps {
  check: DesignerCheck;
  onChange: (updated: DesignerCheck) => void;
  onDelete: () => void;
}

function CheckEditor({ check, onChange, onDelete }: CheckEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-text-primary">Check Constraint Properties</h3>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 px-2 py-1 text-xs text-accent-red hover:bg-accent-red/10 rounded-sm transition-colors"
        >
          <Trash2 size={12} />
          Remove
        </button>
      </div>

      <div>
        <label className={labelClass}>Name</label>
        <input
          type="text"
          value={check.name}
          onChange={(e) => onChange({ ...check, name: e.target.value })}
          placeholder="chk_table_constraint"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Expression</label>
        <textarea
          value={check.expression}
          onChange={(e) => onChange({ ...check, expression: e.target.value })}
          placeholder="e.g. age >= 0"
          rows={3}
          className="w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary font-mono placeholder:text-text-muted outline-none focus:border-focus transition-colors resize-y"
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════

interface TableDesignerProps {
  tab: TableDesignerTab;
}

export function TableDesigner({ tab }: TableDesignerProps) {
  const openTab = useWorkspaceStore((s) => s.openTab);
  const queryClient = useQueryClient();

  // For alter mode, fetch existing table metadata
  const { data: tableData, isLoading } = useQuery<TableStructureResponse>({
    queryKey: ['table-structure', tab.connectionId, tab.schema, tab.tableName],
    queryFn: () =>
      apiClient.get<TableStructureResponse>(
        `/api/schema/${tab.connectionId}/tables/${encodeURIComponent(tab.schema)}/${encodeURIComponent(tab.tableName!)}`,
      ),
    enabled: tab.mode === 'alter' && !!tab.tableName,
    staleTime: 60_000,
  });

  // State
  const [designerState, setDesignerState] = useState<DesignerState>(() => ({
    tableName: tab.tableName ?? '',
    schema: tab.schema,
    columns: [createEmptyColumn()],
    indexes: [],
    foreignKeys: [],
    checks: [],
  }));

  // Original state for alter mode diff
  const originalRef = useRef<DesignerState | null>(null);

  // Hydrate from fetched data in alter mode
  const [hydrated, setHydrated] = useState(tab.mode === 'create');
  useEffect(() => {
    if (tab.mode === 'alter' && tableData && !hydrated) {
      const cols: DesignerColumn[] = tableData.columns.map((c) => ({
        id: `col-${uid()}`,
        name: c.name,
        type: c.type,
        notNull: !c.nullable,
        defaultValue: c.defaultValue ?? '',
        isPrimaryKey: c.isPrimaryKey,
        comment: '',
      }));

      // Extract FKs from column metadata
      const fks: DesignerForeignKey[] = [];
      for (const c of tableData.columns) {
        if (c.isForeignKey && c.referencesTable) {
          fks.push({
            id: `fk-${uid()}`,
            name: `fk_${tableData.name}_${c.name}`,
            columns: [c.name],
            refTable: c.referencesTable,
            refColumns: c.referencesColumn ? [c.referencesColumn] : [],
            onDelete: 'NO ACTION',
            onUpdate: 'NO ACTION',
          });
        }
      }

      const newState: DesignerState = {
        tableName: tableData.name,
        schema: tab.schema,
        columns: cols,
        indexes: [],
        foreignKeys: fks,
        checks: [],
      };

      setDesignerState(newState);
      originalRef.current = JSON.parse(JSON.stringify(newState));
      setHydrated(true);
    }
  }, [tab.mode, tableData, hydrated, tab.schema]);

  const [selection, setSelection] = useState<TreeSelection>(null);
  const [bottomHeight, setBottomHeight] = useState(150);
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState('');
  const [executeSuccess, setExecuteSuccess] = useState('');

  // SQL preview
  const sql = useMemo(() => {
    if (tab.mode === 'alter' && originalRef.current) {
      return generateAlterSQL(designerState, originalRef.current);
    }
    return generateCreateSQL(designerState);
  }, [designerState, tab.mode]);

  // Column names for dropdowns
  const columnNames = useMemo(
    () => designerState.columns.filter((c) => c.name.trim()).map((c) => c.name),
    [designerState.columns],
  );

  // ── Handlers ──

  const updateState = useCallback((fn: (prev: DesignerState) => DesignerState) => {
    setDesignerState(fn);
  }, []);

  const addColumn = useCallback(() => {
    const col = createEmptyColumn();
    updateState((s) => ({ ...s, columns: [...s.columns, col] }));
    setSelection({ section: 'column', id: col.id });
  }, [updateState]);

  const addIndex = useCallback(() => {
    const idx = createEmptyIndex(designerState.tableName);
    updateState((s) => ({ ...s, indexes: [...s.indexes, idx] }));
    setSelection({ section: 'index', id: idx.id });
  }, [updateState, designerState.tableName]);

  const addFK = useCallback(() => {
    const fk = createEmptyFK(designerState.tableName);
    updateState((s) => ({ ...s, foreignKeys: [...s.foreignKeys, fk] }));
    setSelection({ section: 'fk', id: fk.id });
  }, [updateState, designerState.tableName]);

  const addCheck = useCallback(() => {
    const chk = createEmptyCheck(designerState.tableName);
    updateState((s) => ({ ...s, checks: [...s.checks, chk] }));
    setSelection({ section: 'check', id: chk.id });
  }, [updateState, designerState.tableName]);

  const deleteSelected = useCallback(() => {
    if (!selection) return;
    if (selection.section === 'column') {
      updateState((s) => {
        if (s.columns.length <= 1) return s;
        return { ...s, columns: s.columns.filter((c) => c.id !== selection.id) };
      });
    } else if (selection.section === 'index') {
      updateState((s) => ({ ...s, indexes: s.indexes.filter((i) => i.id !== selection.id) }));
    } else if (selection.section === 'fk') {
      updateState((s) => ({ ...s, foreignKeys: s.foreignKeys.filter((f) => f.id !== selection.id) }));
    } else if (selection.section === 'check') {
      updateState((s) => ({ ...s, checks: s.checks.filter((c) => c.id !== selection.id) }));
    }
    setSelection(null);
  }, [selection, updateState]);

  const handleOpenInEditor = useCallback(() => {
    openTab({
      type: 'query',
      id: `query:${tab.connectionId}:${Date.now()}`,
      connectionId: tab.connectionId,
      title: tab.mode === 'create' ? `CREATE ${designerState.tableName}` : `ALTER ${designerState.tableName}`,
      sql,
    });
  }, [openTab, tab.connectionId, tab.mode, designerState.tableName, sql]);

  const handleExecute = useCallback(async () => {
    if (sql.startsWith('--')) return;
    setExecuting(true);
    setExecuteError('');
    setExecuteSuccess('');

    try {
      await apiClient.post('/api/query/execute-multi', {
        connectionId: tab.connectionId,
        sql,
      });
      setExecuteSuccess('Executed successfully');
      queryClient.invalidateQueries({ queryKey: ['schema', tab.connectionId] });
      setTimeout(() => setExecuteSuccess(''), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Execution failed';
      setExecuteError(message);
    } finally {
      setExecuting(false);
    }
  }, [sql, tab.connectionId, queryClient]);

  const handleBottomResize = useCallback((delta: number) => {
    setBottomHeight((prev) => Math.max(60, Math.min(400, prev - delta)));
  }, []);

  // ── Selected item ──

  const selectedColumn = useMemo(() => {
    if (selection?.section !== 'column') return null;
    return designerState.columns.find((c) => c.id === selection.id) ?? null;
  }, [selection, designerState.columns]);

  const selectedIndex = useMemo(() => {
    if (selection?.section !== 'index') return null;
    return designerState.indexes.find((i) => i.id === selection.id) ?? null;
  }, [selection, designerState.indexes]);

  const selectedFK = useMemo(() => {
    if (selection?.section !== 'fk') return null;
    return designerState.foreignKeys.find((f) => f.id === selection.id) ?? null;
  }, [selection, designerState.foreignKeys]);

  const selectedCheck = useMemo(() => {
    if (selection?.section !== 'check') return null;
    return designerState.checks.find((c) => c.id === selection.id) ?? null;
  }, [selection, designerState.checks]);

  // ── Loading state for alter mode ──

  if (tab.mode === 'alter' && isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-text-muted">
        Loading table structure...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-dbgo-workarea">
      {/* Table Name Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0">
        <span className="text-xs font-semibold text-text-secondary">{designerState.schema}.</span>
        <input
          type="text"
          value={designerState.tableName}
          onChange={(e) => updateState((s) => ({ ...s, tableName: e.target.value }))}
          placeholder="table_name"
          className="bg-dbgo-input border rounded-sm px-2.5 py-[5px] text-[13px] text-text-primary font-mono placeholder:text-text-muted outline-none focus:border-focus transition-colors w-60"
        />
        <span className="text-2xs text-text-muted uppercase tracking-wide">
          {tab.mode === 'create' ? 'New Table' : 'Modify Table'}
        </span>
      </div>

      {/* Main content: left tree + right editor */}
      <div className="flex flex-1 min-h-0">
        {/* Left Pane — Object Tree */}
        <div className="w-[180px] flex-shrink-0 bg-dbgo-sidebar border-r overflow-hidden">
          <ObjectTree
            state={designerState}
            selection={selection}
            onSelect={setSelection}
            onAddColumn={addColumn}
            onAddIndex={addIndex}
            onAddFK={addFK}
            onAddCheck={addCheck}
          />
        </div>

        {/* Right Pane — Property Editor + Bottom SQL Preview */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Property Editor */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {!selection && (
              <div className="flex items-center justify-center h-full text-xs text-text-muted">
                Select an item from the tree to edit its properties
              </div>
            )}

            {selectedColumn && (
              <ColumnEditor
                column={selectedColumn}
                onChange={(updated) =>
                  updateState((s) => ({
                    ...s,
                    columns: s.columns.map((c) => (c.id === updated.id ? updated : c)),
                  }))
                }
                onDelete={deleteSelected}
              />
            )}

            {selectedIndex && (
              <IndexEditor
                index={selectedIndex}
                columnNames={columnNames}
                onChange={(updated) =>
                  updateState((s) => ({
                    ...s,
                    indexes: s.indexes.map((i) => (i.id === updated.id ? updated : i)),
                  }))
                }
                onDelete={deleteSelected}
              />
            )}

            {selectedFK && (
              <FKEditor
                fk={selectedFK}
                columnNames={columnNames}
                onChange={(updated) =>
                  updateState((s) => ({
                    ...s,
                    foreignKeys: s.foreignKeys.map((f) => (f.id === updated.id ? updated : f)),
                  }))
                }
                onDelete={deleteSelected}
              />
            )}

            {selectedCheck && (
              <CheckEditor
                check={selectedCheck}
                onChange={(updated) =>
                  updateState((s) => ({
                    ...s,
                    checks: s.checks.map((c) => (c.id === updated.id ? updated : c)),
                  }))
                }
                onDelete={deleteSelected}
              />
            )}
          </div>

          {/* Resize Handle */}
          <ResizeHandle direction="horizontal" onResize={handleBottomResize} />

          {/* Bottom Pane — SQL Preview */}
          <div
            className="flex-shrink-0 bg-dbgo-editor border-t flex flex-col"
            style={{ height: bottomHeight }}
          >
            {/* Toolbar */}
            <div className="flex items-center justify-between px-3 py-1.5 border-b flex-shrink-0">
              <span className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
                SQL Preview
              </span>
              <div className="flex items-center gap-1">
                {executeError && (
                  <span className="text-2xs text-accent-red mr-2 truncate max-w-[200px]" title={executeError}>
                    {executeError}
                  </span>
                )}
                {executeSuccess && (
                  <span className="text-2xs text-accent-green mr-2">
                    {executeSuccess}
                  </span>
                )}
                <CopyButton text={sql} />
                <button
                  onClick={handleOpenInEditor}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover rounded-sm transition-colors"
                  title="Open in Query Editor"
                >
                  <FileCode2 size={13} />
                  Open in Editor
                </button>
                <button
                  onClick={handleExecute}
                  disabled={executing || sql.startsWith('--')}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-accent-green hover:bg-accent-green/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
                  title="Execute SQL"
                >
                  {executing ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Play size={13} />
                  )}
                  Execute
                </button>
              </div>
            </div>
            {/* SQL Content */}
            <pre className="flex-1 overflow-auto px-4 py-2 text-xs font-mono text-text-primary whitespace-pre select-text">
              {sql}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
